import { CellProps } from 'react-table'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import {
  PlainHeader,
  sortableColumn,
  StackedCell,
  StatusDot,
  TableColumn,
} from '../../../../../../components'
import { PareceresActionsCell } from './PareceresActionsCell'
import { InterconsultaSolicitanteList } from '../../core/_models'
import { acoesPendentesSolicitante, SITUACAO_INTERCONSULTA } from '../../core/situacaoInterconsulta'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { useEscopoPareceres } from '../../core/escopoPareceres'

// Situação do ponto de vista do solicitante + a ação que ele tem a fazer (pendência, prazos de
// réplica e desfecho). O texto carrega o significado; a cor do ponto é só reforço. Na lista da
// equipe a ação é de quem solicitou, não de quem está vendo: fica só a situação.
const SituacaoCell = ({ linha }: { linha: InterconsultaSolicitanteList }) => {
  const daEquipe = useEscopoPareceres() === 'equipe'
  const situacao =
    linha.situacaoInterconsulta && SITUACAO_INTERCONSULTA[linha.situacaoInterconsulta]
  const acoes = daEquipe ? [] : acoesPendentesSolicitante(linha)

  // Fora do StackedCell (que corta com reticências): cada ação em sua linha, com quebra, para o
  // prazo não sumir em colunas estreitas.
  return (
    <div>
      {situacao ? (
        <StatusDot label={situacao.label} tone={situacao.tone} />
      ) : (
        <StackedCell primary={linha.estado?.descricao} />
      )}
      {acoes.map((acao) => (
        <span key={acao} className='d-block fs-7 fw-semibold text-gray-800 text-wrap mt-1'>
          {acao}
        </span>
      ))}
    </div>
  )
}

export const PareceresColumns = (
  t: TFunction
): ReadonlyArray<TableColumn<InterconsultaSolicitanteList>> => [
  // Paciente com o protocolo como apoio; os dois seguem ordenáveis.
  sortableColumn<InterconsultaSolicitanteList>({
    id: 'paciente.pessoa.nome',
    useQueryRequest,
    fields: [
      { field: 'paciente.pessoa.nome', label: t('cols.paciente') },
      { field: 'protocolo', label: t('cols.protocolo') },
    ],
    Cell: ({ row }: CellProps<InterconsultaSolicitanteList>) => (
      <StackedCell
        primary={row.original.paciente?.pessoa?.nome}
        secondary={row.original.protocolo}
      />
    ),
  }),
  sortableColumn<InterconsultaSolicitanteList>({
    id: 'especialidade',
    cellWidth: '240px',
    useQueryRequest,
    fields: [{ field: 'especialidade', label: t('cols.especialidade') }],
    Cell: ({ row }: CellProps<InterconsultaSolicitanteList>) => (
      <StackedCell primary={row.original.especialidade?.nome} />
    ),
  }),
  // Agendamento (ordem padrão: mais recente primeiro); o backend já manda formatado.
  sortableColumn<InterconsultaSolicitanteList>({
    id: 'solicitacao',
    cellWidth: '180px',
    useQueryRequest,
    fields: [{ field: 'solicitacao', label: t('cols.agendamento') }],
    Cell: ({ row }: CellProps<InterconsultaSolicitanteList>) => (
      <StackedCell tabular primary={row.original.solicitacaoFormatada} />
    ),
  }),
  {
    Header: () => <PlainHeader>{t('cols.situacao')}</PlainHeader>,
    id: 'situacaoInterconsulta',
    cellWidth: '350px',
    Cell: ({ row }: CellProps<InterconsultaSolicitanteList>) => (
      <SituacaoCell linha={row.original} />
    ),
  },
  {
    Header: () => <PlainHeader>{t('cols.actions')}</PlainHeader>,
    id: 'actions',
    cellWidth: '132px',
    align: 'end',
    Cell: ({ row }: CellProps<InterconsultaSolicitanteList>) => (
      <PareceresActionsCell atendimento={row.original} />
    ),
  },
]
